import { useAui } from '@assistant-ui/react';
import { use, useEffect, useEffectEvent } from 'react';

import { type AssistantAgent, AssistantContext } from '#plugin-chat/contexts/AssistantContext.tsx';

/**
 * Registers an agent with the assistant context and conditionally activates its model context.
 * @param name - Unique identifier for the agent
 * @param agent - Agent instance to register
 * @returns Whether this agent is currently active
 */
export function useAgentRegistration(name: string, agent: AssistantAgent) {
  const aui = useAui();
  const { registerAgent, activeAgent } = use(AssistantContext);
  const registerAgentEvent = useEffectEvent(registerAgent);
  const isActive = activeAgent === name;

  // Register agent with context on mount, automatically unregister on unmount
  useEffect(() => {
    return registerAgentEvent(name, agent);
  }, [name, agent]);

  // Only register model context when this agent becomes active
  useEffect(() => {
    if (!isActive) {
      return;
    }
    return aui.modelContext().register({ getModelContext: agent.getModelContext });
  }, [agent, isActive, aui]);

  return isActive;
}
